import { SHELL_PORTAL } from 'lib/context/ShellContext/shellPortal';

import { userState } from 'components/state';
import { current_admin } from 'components/admin/lib/adminState';
import { currentPractice } from 'components/practice/practiceState';

// Through the effect's own getLoadable, never recoil-nexus's getRecoil: that clones a snapshot,
// cloning initializes this atom again from inside its own initialization, and on a reload with
// saved state the effect recursed until the tab hung.
const readAtom = (getLoadable, atom, pick) => {
  try {
    const loadable = getLoadable(atom);
    return loadable.state === 'hasValue' ? (pick(loadable.contents) ?? null) : null;
  } catch {
    return null;
  }
};

// Whose session each portal's state belongs to: the practice user's, or the admin's, whose
// sessions leave the practice user empty.
export const PORTAL_ACCOUNT = {
  [SHELL_PORTAL.PRACTICE]: userState,
  [SHELL_PORTAL.ADMIN]: current_admin
};

// Both default user_id to '' pre-auth — treat that as unresolved, not a mismatch. Ids may arrive
// as numbers or strings.
export const accountIdOf = (account) => (account?.user_id ? String(account.user_id) : null);

const sameOwner = (savedOwnerId, ownerId) =>
  savedOwnerId != null && String(savedOwnerId) === ownerId;

const writeStamped = (storageKey, value, practiceId, ownerId) =>
  sessionStorage.setItem(storageKey, JSON.stringify({ practiceId, ownerId, value }));

const readStamp = (storageKey) => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey));
    return { practiceId: saved?.practiceId ?? null, ownerId: saved?.ownerId ?? null };
  } catch {
    return { practiceId: null, ownerId: null };
  }
};

const provisional = new Map();

/**
 * Settles every atom restored or written before the practice/user ids resolved: re-stamps the ones
 * that belong to the session that just materialised, drops the ones that do not.
 */
export const revalidateSessionPersistedAtoms = () => {
  provisional.forEach(({ resetSelf, isOwn, identity }, storageKey) => {
    const { practiceId, ownerId } = identity.read();
    if (!identity.isResolved(practiceId, ownerId)) return;

    provisional.delete(storageKey);

    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);

      const belongsHere =
        isOwn ||
        ((!identity.practiceScoped ||
          saved?.practiceId == null ||
          saved.practiceId === practiceId) &&
          sameOwner(saved?.ownerId, ownerId));

      if (belongsHere) {
        writeStamped(storageKey, saved?.value, practiceId, ownerId);
      } else {
        sessionStorage.removeItem(storageKey);
        resetSelf();
      }
    } catch {
      // Corrupt or unavailable storage — leave the atom on whatever it is holding.
    }
  });
};

/**
 * Recoil atom effect mirroring an atom into sessionStorage so it survives a reload.
 * sessionStorage (not localStorage) is deliberate: tab-scoped and cleared on close,
 * so state doesn't leak into a later session on a shared machine.
 *
 * The payload is stamped with the practice id AND the authenticated user id, and restored
 * only on a match: sessionStorage outlives a logout in the same tab, so without the owner
 * tag the next user to sign in on a shared workstation would inherit the previous user's
 * state. A current id that has not resolved yet is provisional, not a match.
 *
 * `sanitize(value)` returns the JSON-serializable slice to persist; applied on both
 * write and restore, so keep it idempotent — never emit refs, functions, or transient fields.
 *
 * `revive(value)` adjusts a value restored from storage, after `sanitize`; whatever it adds must be
 * something `sanitize` drops, so it never reaches storage.
 *
 * `owner` is the portal whose signed-in account the state belongs to. `practiceScoped: false` skips
 * the practice check, for state whose storage key already names its practice or that spans
 * practices.
 *
 * @param {{ storageKey: string, sanitize: (value: any) => any, revive?: (value: any) => any, owner?: 'practice' | 'admin', practiceScoped?: boolean }} config
 */
export const sessionPersistEffect =
  ({ storageKey, sanitize, revive, owner = SHELL_PORTAL.PRACTICE, practiceScoped = true }) =>
  ({ setSelf, onSet, resetSelf, getLoadable, parentStoreID_UNSTABLE }) => {
    const isSnapshot = parentStoreID_UNSTABLE != null;
    // The admin portal has no current practice, so only practice-portal state waits on one.
    const needsPractice = practiceScoped && owner === SHELL_PORTAL.PRACTICE;
    const identity = {
      practiceScoped,
      read: () => ({
        practiceId: readAtom(getLoadable, currentPractice, (p) => p?.id),
        ownerId: readAtom(getLoadable, PORTAL_ACCOUNT[owner], accountIdOf)
      }),
      isResolved: (practiceId, ownerId) => ownerId != null && (!needsPractice || practiceId != null)
    };

    try {
      const raw = sessionStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        const { practiceId, ownerId } = identity.read();

        const practiceMatches =
          !practiceScoped ||
          practiceId == null ||
          saved?.practiceId == null ||
          saved.practiceId === practiceId;
        // A stored blob with no owner predates this tag — drop it rather than hand it
        // to whoever is signed in now.
        const ownerMatches = ownerId == null || sameOwner(saved?.ownerId, ownerId);

        if (practiceMatches && ownerMatches) {
          const restored = sanitize(saved?.value);
          if (restored !== undefined) setSelf(revive ? revive(restored) : restored);
          if (!isSnapshot && !identity.isResolved(practiceId, ownerId)) {
            provisional.set(storageKey, { resetSelf, isOwn: false, identity });
          }
        } else if (!isSnapshot) {
          sessionStorage.removeItem(storageKey);
        }
      }
    } catch {
      // Corrupt or unavailable storage — fall back to the atom's default.
    }

    if (isSnapshot) return;

    onSet((newValue, _oldValue, isReset) => {
      try {
        if (isReset) {
          provisional.delete(storageKey);
          sessionStorage.removeItem(storageKey);
          return;
        }

        const { practiceId, ownerId } = identity.read();

        if (identity.isResolved(practiceId, ownerId)) {
          writeStamped(storageKey, sanitize(newValue), practiceId, ownerId);
          provisional.delete(storageKey);
          return;
        }

        const prior = readStamp(storageKey);
        writeStamped(storageKey, sanitize(newValue), prior.practiceId, prior.ownerId);
        if (!provisional.has(storageKey)) {
          provisional.set(storageKey, { resetSelf, isOwn: true, identity });
        }
      } catch {
        // Storage full / unavailable — persistence is best-effort, never fatal.
      }
    });
  };

// Removes `storageKey` and every key under it (`storageKey:…`), including ones no atom has loaded.
export const forgetSessionState = (storageKey) => {
  try {
    const keys = Array.from({ length: sessionStorage.length }, (_, index) =>
      sessionStorage.key(index)
    );
    keys
      .filter((key) => key === storageKey || key?.startsWith(`${storageKey}:`))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was persisted.
  }
};
